import { useState } from 'react'
import {
  DROPDOWN_HOUSING_FORMS,
  HOUSING_FORM_LABELS,
  type DocKind,
  type FeasibilityRequest,
  type HousingForm,
  type PendingDoc,
} from '../api'

type UploadPhase = 'idle' | 'uploading' | 'queued' | 'failed'

type TitleRow = { lot: string; dp: string }

type DocSlot = {
  endpoint: string                  // /api/uploads/cert | /api/uploads/contract
  label: string                     // "s10.7 PDF" | "Contract of sale PDF"
  prettyName: string                // "Section 10.7 certificate" | "Contract of sale"
  kind: DocKind
}

const CERT_SLOT: DocSlot = {
  endpoint: '/api/uploads/cert',
  label: 'Choose s10.7 PDF…',
  prettyName: 'Section 10.7 certificate',
  kind: 'cert',
}

const CONTRACT_SLOT: DocSlot = {
  endpoint: '/api/uploads/contract',
  label: 'Choose contract PDF…',
  prettyName: 'Contract of sale',
  kind: 'contract',
}

export function AddressForm({
  onSubmit,
  loading,
}: {
  onSubmit: (req: FeasibilityRequest, pendingDocs: PendingDoc[]) => Promise<void> | void
  loading: boolean
}) {
  const [address, setAddress] = useState('')
  // Lot and DP live in separate inputs because every NSW title is
  // identified by two distinct numbers — collapsing them into one
  // "Lot/DP" field made users guess the punctuation. We only send a
  // combined `lot_dp` query / payload value when BOTH are filled,
  // otherwise we leave the parameter out and let the orchestrator
  // resolve the title from the address (or the parser lift it off
  // the first page of an uploaded document).
  // One row per title. A sale often bundles a second lot or a
  // closed-road strip with the house lot (Five Dock: Lot A DP 396594
  // + Lot 462 DP 752023); each gets its own Lot + DP pair rather than
  // a punctuation convention in one field.
  const [titles, setTitles] = useState<TitleRow[]>([{ lot: '', dp: '' }])
  const updateTitle = (i: number, key: keyof TitleRow, v: string) =>
    setTitles((rows) => rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)))
  const addTitle = () => setTitles((rows) => [...rows, { lot: '', dp: '' }])
  const removeTitle = (i: number) => setTitles((rows) => rows.filter((_, j) => j !== i))
  const [siteArea, setSiteArea] = useState('')
  const [frontage, setFrontage] = useState('')
  const [housingForm, setHousingForm] = useState<HousingForm>('dwelling_house')
  const [storeys, setStoreys] = useState('')
  // Low and Mid-Rise Housing area status, read by the planner off the
  // Department's indicative map. Empty = not sure → the report says
  // "verify" for the Housing SEPP duplex standards.
  const [lmrArea, setLmrArea] = useState<'' | 'inside' | 'outside'>('')
  // Second road access, read off the deposited plan. The road layer
  // rarely resolves a rear lane or a through-lot, and it changes the
  // duplex answer (12 m width, front-and-back layout, Torrens split).
  const [rearAccess, setRearAccess] = useState<'' | 'lane' | 'street' | 'corner'>('')
  // Each optional document carries its own file + phase + message. After
  // a successful POST the file is "queued" — the actual parse runs in
  // the background and is tracked by the parent App via the returned
  // job_id. We don't poll here anymore (the form is hidden once the
  // user submits) — App owns reload of the report once the cert /
  // contract finishes reviewing.
  const [certFile, setCertFile] = useState<File | null>(null)
  const [contractFile, setContractFile] = useState<File | null>(null)
  const [certPhase, setCertPhase] = useState<UploadPhase>('idle')
  const [contractPhase, setContractPhase] = useState<UploadPhase>('idle')
  const [certMsg, setCertMsg] = useState<string | null>(null)
  const [contractMsg, setContractMsg] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Defaulted ON: most planners submit, close the tab, and want to
  // come back to a finished report via the email link rather than
  // sitting on the page waiting for the ~3-min analyst pass. Opt
  // out for the "wait on screen" workflow.
  const [emailWhenReady, setEmailWhenReady] = useState(true)
  // Optional override for who the notification goes to. When blank
  // the backend falls back to the signed-in Supabase email. Useful
  // for sending the report to a colleague, consultant, or owner.
  const [notifyEmail, setNotifyEmail] = useState('')

  const inputCls =
    'w-full border border-massing-rule bg-massing-paper-deep px-3 py-2 text-massing-ink placeholder:text-massing-ink-light/70 focus:outline-none focus:border-massing-highlight'
  const labelCls = 'block brand-eyebrow mb-1.5'

  // Combined lot/DP string we send to the server. When either half is
  // empty we return null — the server treats `lot_dp` as optional on
  // every endpoint, falling back to address-derived cadastre lookup
  // or to whatever the document's cover page says.
  const combinedLotDp = (): string | null => {
    const refs = titles
      .map((t) => ({ lot: t.lot.trim(), dp: t.dp.trim() }))
      .map((t) => {
        // A full reference pasted into the Lot box ("A/396594") still
        // works; otherwise a row counts only when both halves are in.
        if (t.lot.includes('/')) return t.lot
        return t.lot && t.dp ? `${t.lot}/${t.dp}` : ''
      })
      .filter(Boolean)
    // Several titles travel as "A/396594; 462/752023" — the server
    // parses every pair and unions the parcels into one site.
    return refs.length ? refs.join('; ') : null
  }

  async function uploadAndGetJobId(
    slot: DocSlot,
    file: File,
    setPhase: (p: UploadPhase) => void,
    setMsg: (m: string | null) => void,
  ): Promise<PendingDoc | null> {
    setPhase('uploading')
    setMsg(`Sending ${file.name} over to the planning team…`)
    try {
      const fd = new FormData()
      fd.append('file', file)
      // Only attach the lot/DP query param when the planner actually
      // filled in both halves. The server will read the lot/DP off the
      // document itself when we leave it out — first page of every
      // NSW 10.7 / contract of sale carries the title reference.
      const combined = combinedLotDp()
      const qs = combined ? `?${new URLSearchParams({ lot_dp: combined })}` : ''
      const r = await fetch(`${slot.endpoint}${qs}`, { method: 'POST', body: fd })
      const raw = await r.text()
      if (!r.ok) {
        let detail = raw
        try {
          const parsed = JSON.parse(raw)
          if (parsed?.detail) {
            detail = typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail)
          }
        } catch { /* not JSON */ }
        setMsg(`${slot.prettyName} upload failed (${r.status}): ${detail}`)
        setPhase('failed')
        return null
      }
      let job: { job_id?: string } = {}
      try { job = JSON.parse(raw) } catch { /* unparseable */ }
      const jobId = job.job_id
      if (!jobId) {
        setMsg(`${slot.prettyName} uploaded but no job ID returned. Running feasibility without it.`)
        setPhase('failed')
        return null
      }
      setMsg(`${file.name} is being reviewed in the background — your report will fill itself in with the findings as soon as they land.`)
      setPhase('queued')
      return { jobId, kind: slot.kind, fileName: file.name }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e))
      setPhase('failed')
      return null
    }
  }

  const docsUploading = certPhase === 'uploading' || contractPhase === 'uploading'

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault()
        if (!address.trim()) return
        setSubmitting(true)
        // Kick off uploads in parallel — these return AS SOON as the
        // multipart POST lands (1–3 s), with a job_id pointing at the
        // background review. Feasibility starts running while the docs
        // OCR + extract; the parent App fills the report in when the
        // jobs finish. Failed uploads simply don't get a job_id and
        // the run proceeds without that document.
        const [certPending, contractPending] = await Promise.all([
          certFile ? uploadAndGetJobId(CERT_SLOT, certFile, setCertPhase, setCertMsg) : Promise.resolve(null),
          contractFile ? uploadAndGetJobId(CONTRACT_SLOT, contractFile, setContractPhase, setContractMsg) : Promise.resolve(null),
        ])
        const pending: PendingDoc[] = [certPending, contractPending].filter(
          (d): d is PendingDoc => d !== null,
        )
        try {
          await onSubmit({
            address: address.trim(),
            lot_dp: combinedLotDp(),
            site_area_m2: siteArea ? Number(siteArea) : null,
            frontage_m: frontage ? Number(frontage) : null,
            housing_form: housingForm,
            intended_storeys: storeys ? Number(storeys) : null,
            // Only opt into cert/contract cache lookup when the user
            // actually picked a doc in this session. Without this gate,
            // a cert uploaded for this lot in any previous session
            // would resurface and the report would falsely claim
            // "via s10.7" verification on data the user didn't supply.
            apply_uploaded_docs: !!(certFile || contractFile),
            email_when_ready: emailWhenReady,
            notify_email: notifyEmail.trim() || null,
            lmr_area: lmrArea || null,
            rear_access: rearAccess || null,
          }, pending)
        } finally {
          setSubmitting(false)
        }
      }}
      className="glass p-6 rounded-md"
    >
      <label className={labelCls}>NSW address</label>
      <input
        type="text"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="e.g. 30 Albert Drive Killara"
        className={`${inputCls} font-serif text-lg mb-4`}
        required
      />
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Field label="Lot" value={titles[0].lot} setValue={(v) => updateTitle(0, 'lot', v)} placeholder="7 or A" inputMode="text" inputCls={inputCls} labelCls={labelCls} />
        <Field label="DP / SP" value={titles[0].dp} setValue={(v) => updateTitle(0, 'dp', v)} placeholder="12345" inputMode="numeric" inputCls={inputCls} labelCls={labelCls} />
        <Field label="Site area (m²)" value={siteArea} setValue={setSiteArea} placeholder="950" inputMode="numeric" inputCls={inputCls} labelCls={labelCls} />
        <Field label="Frontage (m)" value={frontage} setValue={setFrontage} placeholder="18" inputMode="numeric" inputCls={inputCls} labelCls={labelCls} />
        <div>
          <label className={labelCls}>Housing form</label>
          <select
            value={housingForm}
            onChange={(e) => setHousingForm(e.target.value as HousingForm)}
            className={inputCls}
          >
            {DROPDOWN_HOUSING_FORMS.map((k) => [k, HOUSING_FORM_LABELS[k]] as [HousingForm, string]).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>
        <Field label="Storeys" value={storeys} setValue={setStoreys} placeholder="2" inputMode="numeric" inputCls={inputCls} labelCls={labelCls} />
      </div>

      {/* Extra titles — a second lot or a closed-road strip sold with
          the house. Each is its own Lot + DP pair; the server fetches
          every parcel and unions them into one site. */}
      {titles.slice(1).map((t, k) => {
        const i = k + 1
        return (
          <div key={i} className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-4 items-end">
            <Field label={`Lot (title ${i + 1})`} value={t.lot} setValue={(v) => updateTitle(i, 'lot', v)} placeholder="462" inputMode="text" inputCls={inputCls} labelCls={labelCls} />
            <Field label={`DP / SP (title ${i + 1})`} value={t.dp} setValue={(v) => updateTitle(i, 'dp', v)} placeholder="752023" inputMode="numeric" inputCls={inputCls} labelCls={labelCls} />
            <button
              type="button"
              onClick={() => removeTitle(i)}
              className="justify-self-start pb-2.5 text-[11px] uppercase tracking-wide text-massing-ink-light hover:text-massing-ink"
            >
              Remove title
            </button>
          </div>
        )
      })}
      <button
        type="button"
        onClick={addTitle}
        className="mt-2 text-[11.5px] text-massing-ink-light hover:text-massing-ink underline underline-offset-2"
      >
        + Add another title (a second lot or closed-road strip sold with the house)
      </button>

      {/* Low and Mid-Rise Housing area. The NSW spatial layer isn't
          published, so the planner reads the Department's indicative
          map and tells us. "Inside" turns on the Housing SEPP duplex
          DA standards (450 m² / 12 m) and waives the LEP minimum lot
          for a Pattern Book duplex; the report says who supplied it. */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
        <div>
          <label className={labelCls}>Low and Mid-Rise Housing area</label>
          <select
            value={lmrArea}
            onChange={(e) => setLmrArea(e.target.value as '' | 'inside' | 'outside')}
            className={inputCls}
          >
            <option value="">Not sure — report will say "verify"</option>
            <option value="inside">Inside (checked on the NSW indicative map)</option>
            <option value="outside">Outside</option>
          </select>
          <p className="mt-1.5 text-[11.5px] text-massing-ink-light leading-snug">
            From the Department&apos;s Low and Mid-Rise Housing Policy map. Inside,
            a duplex gets the Housing SEPP DA standards (450 m² / 12 m) and the
            LEP minimum lot is waived for a Pattern Book duplex.
          </p>
        </div>
        <div>
          <label className={labelCls}>Second road access</label>
          <select
            value={rearAccess}
            onChange={(e) => setRearAccess(e.target.value as '' | 'lane' | 'street' | 'corner')}
            className={inputCls}
          >
            <option value="">None — one street frontage</option>
            <option value="lane">Rear lane (public road 3–7 m wide)</option>
            <option value="street">Second street — lot runs through the block</option>
            <option value="corner">Corner lot — secondary road</option>
          </select>
          <p className="mt-1.5 text-[11.5px] text-massing-ink-light leading-snug">
            From the deposited plan. Parking off a lane or second road drops the
            Codes SEPP duplex width to 12 m; a second street also allows one
            dwelling behind the other and a front-and-back Torrens split.
          </p>
        </div>
        <p className="text-[11.5px] text-massing-ink-light leading-snug md:pt-6">
          Both answers are recorded in the report as supplied by you and not
          independently verified. Several titles in one sale? Use &ldquo;Add
          another title&rdquo; above so each lot and plan is entered on its
          own row.
        </p>
      </div>

      <div className="mt-6 pt-5 border-t border-massing-rule-soft">
        <div className="brand-eyebrow mb-2">Optional documents</div>
        <p className="text-[12px] text-massing-ink-light mb-3 max-w-2xl">
          The <strong>s10.7 certificate</strong> lets us replace inferred overlays
          (heritage, bushfire, flood) and the explicit CDC eligibility statement
          with council's own answer. The <strong>contract of sale</strong> + 88B
          instrument adds easements, restrictive covenants, and any special
          conditions that constrain what can be built. Both review in the
          background — the report opens immediately and fills itself in
          the moment the findings land.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DocPicker
            slot={CERT_SLOT}
            file={certFile}
            setFile={(f) => {
              setCertFile(f)
              if (f) {
                setCertPhase('idle')
                setCertMsg(`${f.name} ready — will start the moment you run.`)
              } else {
                setCertPhase('idle')
                setCertMsg(null)
              }
            }}
            phase={certPhase}
            msg={certMsg}
          />
          <DocPicker
            slot={CONTRACT_SLOT}
            file={contractFile}
            setFile={(f) => {
              setContractFile(f)
              if (f) {
                setContractPhase('idle')
                setContractMsg(`${f.name} ready — will start the moment you run.`)
              } else {
                setContractPhase('idle')
                setContractMsg(null)
              }
            }}
            phase={contractPhase}
            msg={contractMsg}
          />
        </div>
      </div>

      {/* Email-when-ready toggle. The analyst pass runs in the
          background (~30-90s, occasionally longer); planners can
          close the tab and come back via the email link rather
          than wait on screen. On by default. */}
      <label className="mt-6 flex items-start gap-3 cursor-pointer text-sm text-massing-ink">
        <input
          type="checkbox"
          checked={emailWhenReady}
          onChange={(e) => setEmailWhenReady(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-massing"
        />
        <span>
          <span className="font-medium">Email the link when the analysis is ready</span>
          <span className="block text-[12px] text-massing-ink-light mt-0.5">
            The report opens immediately with the Section B controls
            and CDC verdict — the findings analysis layer fills in
            after a short background pass. Uncheck to wait on screen.
          </span>
        </span>
      </label>

      {/* Optional explicit recipient. When blank the notification
          goes to the signed-in user's Supabase email; supply a
          different address here to send the report to a colleague,
          consultant, or property owner. Only relevant when the
          checkbox above is on. */}
      {emailWhenReady && (
        <div className="mt-3 ml-7">
          <label className="block text-[12px] text-massing-ink-light mb-1">
            Send to (optional — defaults to your account email)
          </label>
          <input
            type="email"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="name@example.com"
            className="w-full max-w-md text-sm bg-massing-card border border-massing-rule text-massing-ink px-3 py-2 rounded-sm focus:outline-none focus:border-massing-highlight"
            autoComplete="email"
          />
        </div>
      )}

      <div className="mt-5 flex items-center gap-4">
        <button
          type="submit"
          disabled={loading || submitting || docsUploading}
          className="bg-massing hover:bg-massing-deep text-massing-cream font-medium tracking-wide px-6 py-2.5 disabled:opacity-50 transition-colors"
        >
          {docsUploading ? 'Sending documents…' : (loading || submitting ? 'Running…' : 'Run feasibility')}
        </button>
        <p className="text-xs text-massing-ink-light">
          Returns Sections A–I with confidence-flagged values and citations. PDF available after running.
        </p>
      </div>
    </form>
  )
}

function DocPicker({
  slot,
  file,
  setFile,
  phase,
  msg,
}: {
  slot: DocSlot
  file: File | null
  setFile: (f: File | null) => void
  phase: UploadPhase
  msg: string | null
}) {
  // Phase pills give the user an honest read on what's happening
  // without parsing message strings. `Picked` lights up the moment a
  // file is selected; `Uploading` covers the multipart POST; `Queued`
  // means the document is now being reviewed in the background — the
  // parent App takes over from here and surfaces the parse status on
  // the report page itself.
  const stages: Array<{ key: UploadPhase; label: string }> = [
    { key: 'idle', label: 'Picked' },
    { key: 'uploading', label: 'Uploading' },
    { key: 'queued', label: 'Queued' },
  ]
  const failed = phase === 'failed'
  const queued = phase === 'queued'
  const active = phase === 'uploading'
  return (
    <div
      className={
        'rounded-md border p-3 bg-massing-cream/40 transition-colors ' +
        (failed ? 'border-flag-red/50' :
         queued ? 'border-flag-green/50' :
         active ? 'border-flag-amber/50' :
         'border-massing-rule-soft')
      }
    >
      <div className="flex items-center gap-2 text-sm">
        <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
          <span className="px-3 py-1.5 border border-massing-rule rounded-md bg-massing-paper-deep hover:border-massing-highlight transition-colors truncate">
            {file ? file.name : slot.label}
          </span>
          <input
            type="file"
            accept=".pdf,.html,.htm,.txt"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              setFile(f)
              e.target.value = ''
            }}
          />
        </label>
        {file && phase !== 'uploading' && (
          <button
            type="button"
            onClick={() => setFile(null)}
            className="shrink-0 px-2 py-1 text-[11px] uppercase tracking-wide border border-massing-rule rounded-md text-massing-ink-light hover:text-massing-ink hover:border-massing-highlight transition-colors"
            aria-label={`Remove ${slot.prettyName}`}
          >
            Remove
          </button>
        )}
      </div>
      {file && (
        <div className="mt-2 flex items-center gap-1.5">
          {stages.map((s) => {
            const reached =
              (s.label === 'Picked' && file != null) ||
              (s.label === 'Uploading' && (active || queued)) ||
              (s.label === 'Queued' && queued)
            const isCurrent = s.label === 'Uploading' && active
            const cls = failed
              ? 'bg-flag-red/30 text-massing-ink/60'
              : reached && s.label === 'Queued'
              ? 'bg-flag-green on-flag'
              : reached
              ? 'bg-massing-highlight/40 text-massing-ink'
              : 'surface-shade-md text-massing-ink-light'
            return (
              <span
                key={s.label}
                className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium ${cls} ${isCurrent ? 'animate-pulse' : ''}`}
              >
                {s.label}
              </span>
            )
          })}
          {failed && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium bg-flag-red text-[#0a0c1a]">
              Failed
            </span>
          )}
        </div>
      )}
      {msg && (
        <div className={`mt-2 text-[12px] ${failed ? 'text-massing-maroon' : 'text-massing-ink-mid'} italic leading-snug`}>
          {msg}
        </div>
      )}
    </div>
  )
}

function Field({
  id, label, value, setValue, placeholder, inputMode, inputCls, labelCls, warning,
}: {
  id?: string
  label: string
  value: string
  setValue: (v: string) => void
  placeholder?: string
  inputMode?: 'numeric' | 'text'
  inputCls: string
  labelCls: string
  warning?: string | null
}) {
  return (
    <div id={id}>
      <label className={labelCls}>{label}</label>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        className={
          inputCls + (warning ? ' border-flag-red/70 focus:border-flag-red' : '')
        }
      />
      {warning && (
        <div className="mt-1 text-[11px] text-massing-maroon italic leading-snug">
          {warning}
        </div>
      )}
    </div>
  )
}
